import CloseIcon from '@material-design-icons/svg/filled/close.svg?react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { ConfirmationDialog } from '../ConfirmationDialog';
import { LoadingIcon } from '../LoadingIcon';
import { usePublisher } from '../../hooks/usePublisher';
import { PUBLISHER_FROZEN_ACTION_TOOLTIP } from '../../utils/publisherFreeze';
import { getPublisherVerificationGate } from '../../utils/publisherVerification';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

interface NewDappPageHeaderProps {
  isFormTouched: boolean;
  isFormValid: boolean;
  isSubmitting?: boolean;
}

export function NewDappPageHeader({
  isFormTouched,
  isFormValid,
  isSubmitting = false,
}: NewDappPageHeaderProps) {
  const navigate = useNavigate();
  const wallet = useWallet();
  const { activePublisher } = usePublisher();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleClose = () => {
    if (isFormTouched) {
      setShowConfirmDialog(true);
    } else {
      navigate(-1);
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmDialog(false);
    navigate(-1);
  };

  const handleCancelClose = () => {
    setShowConfirmDialog(false);
  };

  const isWalletConnected = wallet.connected && wallet.publicKey;
  const isPublisherFrozen = activePublisher?.isFrozen === true;
  const publisherVerification = getPublisherVerificationGate({
    status: activePublisher?.kybStatus,
    publisherType: activePublisher?.publisherType,
    kycKybBypassEnabled: activePublisher?.kycKybBypassEnabled,
  });
  const isButtonDisabled =
    !isFormValid ||
    isSubmitting ||
    !isWalletConnected ||
    isPublisherFrozen ||
    !publisherVerification.isApproved;
  const disabledTooltip = isPublisherFrozen
    ? PUBLISHER_FROZEN_ACTION_TOOLTIP
    : !publisherVerification.isApproved
      ? publisherVerification.actionMessage
      : !isWalletConnected && !isSubmitting
        ? 'Connect a wallet to save your dApp'
        : null;

  const saveButton = (
    <button
      type="submit"
      form="dapp-form"
      disabled={isButtonDisabled}
      className="bg-teal-600 text-white font-medium py-2 px-6 rounded-lg disabled:opacity-50 disabled:hover:bg-teal-600 disabled:pointer-events-none hover:bg-teal-700 relative min-w-[90px] flex items-center justify-center"
      style={{ cursor: isButtonDisabled ? 'default' : 'pointer' }}
    >
      {isSubmitting ? (
        <LoadingIcon label="Submitting" className="size-5 text-white" />
      ) : (
        'Save'
      )}
    </button>
  );

  return (
    <>
      <div className="p-4 bg-neutral-800 flex justify-between items-center text-white">
        <button
          type="button"
          className="text-white p-2 rounded-full hover:bg-neutral-700"
          onClick={handleClose}
        >
          <CloseIcon className="fill-white" />
        </button>
        <div className="text-xl font-semibold tracking-tight whitespace-nowrap">
          Solana dApp Store
        </div>
        <TooltipProvider>
          {disabledTooltip ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">{saveButton}</span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{disabledTooltip}</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            saveButton
          )}
        </TooltipProvider>
      </div>

      <ConfirmationDialog
        isOpen={showConfirmDialog}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave this page?"
        confirmText="Leave"
        cancelText="Stay"
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </>
  );
}
